// Admin users page — list users + create another super_admin.
//
// Only super_admins reach this page (admin-api enforces the role). The
// "Create Super Admin" modal lets one super-admin onboard another by email.
// Endpoint: POST /admin/users/create-super-admin returns the temp password
// once so the inviting admin can share it through a secure channel.

import { useMemo, useState, type JSX, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminFetch, ApiError } from "../lib/api.js";
import { AdminNav } from "../components/AdminNav.js";
import { UserRow, USER_ROLES, type AdminUser } from "../components/UserRow.js";
import { ResetMfaModal } from "../components/ResetMfaModal.js";
import { listTenants, type TenantSummary } from "../lib/adminApiClient.js";

interface UserMeta { page: number; limit: number; total: number; }
interface UserListResponse { data: AdminUser[]; meta: UserMeta; }

interface CreateSuperAdminResponse {
  _id: string;
  email: string;
  full_name: string;
  role: string;
  temp_password: string;
  message: string;
}

const QK_USERS = "admin-users" as const;

function useAdminUsers(tenantId: string, role: string): ReturnType<typeof useQuery<UserListResponse>> {
  const params = new URLSearchParams({ limit: "100" });
  if (tenantId) params.set("tenant_id", tenantId);
  if (role)     params.set("role", role);
  return useQuery<UserListResponse>({
    // Filters are part of the key so a change refetches; UserRow's
    // invalidateQueries([QK_USERS]) still matches by prefix.
    queryKey: [QK_USERS, tenantId, role],
    queryFn:  () => adminFetch<UserListResponse>(`/admin/users?${params.toString()}`),
  });
}

type TenantListResponse = { data: TenantSummary[]; meta: { total: number } };
function useTenantSummaries(): ReturnType<typeof useQuery<TenantListResponse>> {
  // Shares the TenantListPage cache (same key) so the dropdown is usually warm.
  return useQuery<TenantListResponse>({ queryKey: ["super-admin-tenants"], queryFn: listTenants });
}

function UserFilters({ tenants, tenantId, role, onTenant, onRole }: {
  tenants:  TenantSummary[];
  tenantId: string;
  role:     string;
  onTenant: (v: string) => void;
  onRole:   (v: string) => void;
}): JSX.Element {
  const selectCls = "ml-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
  return (
    <div className="flex flex-wrap items-center gap-4">
      <label className="text-sm text-slate-500">Agency
        <select aria-label="Filter by agency" value={tenantId} onChange={(e) => onTenant(e.target.value)} className={selectCls}>
          <option value="">All agencies</option>
          {tenants.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      </label>
      <label className="text-sm text-slate-500">Role
        <select aria-label="Filter by role" value={role} onChange={(e) => onRole(e.target.value)} className={selectCls}>
          <option value="">All roles</option>
          {USER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function -- modal with form, TOTP setup, and recovery codes
function CreateSuperAdminModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [email,    setEmail]    = useState("");
  const [fullName, setFullName] = useState("");
  const [error,    setError]    = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result,   setResult]   = useState<CreateSuperAdminResponse | null>(null);
  const [copied,   setCopied]   = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await adminFetch<CreateSuperAdminResponse>("/admin/users/create-super-admin", {
        method: "POST",
        body:   JSON.stringify({ email: email.trim().toLowerCase(), full_name: fullName.trim() }),
      });
      setResult(data);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (err.status === 409) setError("A user with this email already exists.");
        else if (typeof body?.error === "string") setError(body.error);
        else setError("Could not create super admin.");
      } else {
        setError("Network error. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function copyTempPassword(): void {
    if (!result) return;
    navigator.clipboard.writeText(result.temp_password)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => { /* clipboard unavailable */ });
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        {result ? (
          <>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Super admin created</h2>
            <p className="text-sm text-slate-500 mb-4">
              Share the temporary password with <strong>{result.email}</strong> through a secure channel.
              They'll be forced to change it on first login and set up TOTP.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              <div className="text-xs text-slate-500 mb-1">Temporary password</div>
              <code className="block text-base font-mono font-semibold text-slate-900 break-all">
                {result.temp_password}
              </code>
            </div>
            <div className="flex gap-2">
              <button
                onClick={copyTempPassword}
                className="flex-1 py-2 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
                {copied ? "✅ Copied!" : "Copy password"}
              </button>
              <button
                onClick={() => { onCreated(); onClose(); }}
                className="flex-1 py-2 px-3 rounded-lg bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800">
                Done
              </button>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
              ⚠️ This password will not be shown again.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Create super admin</h2>
            {error && (
              <p role="alert" className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
              <div>
                <label htmlFor="sa-name" className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
                <input
                  id="sa-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="Jane Doe"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="sa-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  id="sa-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="jane@example.com"
                  autoComplete="off"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={onClose} disabled={submitting}
                  className="flex-1 py-2 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2 px-4 rounded-lg bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-50">
                  {submitting ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function UsersTable({ users, tenantNameById, ready, isLoading, isError, onResetMfa }: {
  users:          AdminUser[];
  tenantNameById: Record<string, string>;
  ready:          boolean;
  isLoading:      boolean;
  isError:        boolean;
  onResetMfa:     (u: AdminUser) => void;
}): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      {isLoading && (
        <p className="py-10 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>
      )}
      {isError && (
        <p role="alert" className="py-10 text-center text-sm text-red-600">Failed to load users.</p>
      )}
      {ready && users.length === 0 && (
        <p className="py-10 text-center text-sm text-slate-400">No users found.</p>
      )}
      {ready && users.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="py-3 px-4 font-medium">Email</th>
                <th className="py-3 pr-4 font-medium">Role</th>
                <th className="py-3 pr-4 font-medium">Agency</th>
                <th className="py-3 pr-4 font-medium">Status</th>
                <th className="py-3 pr-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {users.map((u) => (
                <UserRow key={u._id} user={u} tenantName={tenantNameById[u.tenant_id]} onResetMfa={onResetMfa} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line complexity -- nullish-coalescing + conditional renders (filters, count, two modals)
export function AdminUsersPage(): JSX.Element {
  const qc = useQueryClient();
  const [tenantFilter, setTenantFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const { data, isLoading, isError } = useAdminUsers(tenantFilter, roleFilter);
  const { data: tenantsData } = useTenantSummaries();
  const tenants = tenantsData?.data ?? [];
  const tenantNameById = useMemo<Record<string, string>>(
    () => Object.fromEntries((tenantsData?.data ?? []).map((t) => [t._id, t.name])),
    [tenantsData],
  );
  const users = data?.data ?? [];
  const total = data?.meta.total ?? users.length;
  const ready = !isLoading && !isError;
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNav />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">Users</h1>
          <button
            onClick={() => setShowCreate(true)}
            className="py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800"
          >
            + Create super admin
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <UserFilters
            tenants={tenants}
            tenantId={tenantFilter}
            role={roleFilter}
            onTenant={setTenantFilter}
            onRole={setRoleFilter}
          />
          {ready && (
            <p className="text-xs text-slate-400">
              {users.length}{total > users.length ? ` of ${total}` : ""} user{users.length === 1 ? "" : "s"}
            </p>
          )}
        </div>

        <UsersTable
          users={users}
          tenantNameById={tenantNameById}
          ready={ready}
          isLoading={isLoading}
          isError={isError}
          onResetMfa={setResetTarget}
        />
      </div>

      {showCreate && (
        <CreateSuperAdminModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { void qc.invalidateQueries({ queryKey: [QK_USERS] }); }}
        />
      )}

      {resetTarget && (
        <ResetMfaModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onSuccess={() => { void qc.invalidateQueries({ queryKey: [QK_USERS] }); }}
        />
      )}
    </div>
  );
}
